import { computed, type CSSProperties, onBeforeUnmount, onMounted, ref, shallowRef } from "vue";

const STORAGE_KEY = "realtime-controls-position";
const EDGE_MARGIN = 12;
const SNAP_LEFT_THRESHOLD = 0.33;
const SNAP_RIGHT_THRESHOLD = 0.67;
const DRAG_THRESHOLD_PX = 4;

type SnapMode = "edge" | "corner";

interface DraggableOptions {
    storageKey?: string;
    snapMode?: SnapMode;
    zIndex?: number;
}

export function useDraggable(opts: DraggableOptions = {}) {
    const key = opts.storageKey ?? STORAGE_KEY;
    const snapMode = opts.snapMode ?? "edge";
    const zIndex = opts.zIndex ?? 20;

    const elRef = shallowRef<HTMLElement | null>(null);
    const isDragging = ref(false);

    const posX = ref<number | null>(null);
    const posY = ref<number | null>(null);

    try {
        const saved = localStorage.getItem(key);
        if (saved) {
            const { x, y } = JSON.parse(saved) as { x: number; y: number };
            posX.value = x;
            posY.value = y;
        }
    } catch {
        // ignore
    }

    let startX = 0;
    let startY = 0;
    let startPosX = 0;
    let startPosY = 0;
    let hasMoved = false;

    function getClientXY(e: MouseEvent | TouchEvent): { cx: number; cy: number } {
        if ("touches" in e && e.touches.length) {
            return { cx: e.touches[0].clientX, cy: e.touches[0].clientY };
        }
        const me = e as MouseEvent;
        return { cx: me.clientX, cy: me.clientY };
    }

    function initPosition(): void {
        if (posX.value !== null) {
            return;
        }
        const el = elRef.value;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        posX.value = rect.left;
        posY.value = rect.top;
    }

    function clamp(val: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, val));
    }

    function snapToEdge(): void {
        const el = elRef.value;
        if (!el || posX.value === null || posY.value === null) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const centerX = posX.value + rect.width / 2;
        const ratio = centerX / vw;

        if (ratio < SNAP_LEFT_THRESHOLD) {
            posX.value = EDGE_MARGIN;
        } else if (ratio > SNAP_RIGHT_THRESHOLD) {
            posX.value = vw - rect.width - EDGE_MARGIN;
        } else {
            posX.value = (vw - rect.width) / 2;
        }
        posY.value = clamp(posY.value, EDGE_MARGIN, vh - rect.height - EDGE_MARGIN);

        persist();
    }

    function snapToCorner(): void {
        const el = elRef.value;
        if (!el || posX.value === null || posY.value === null) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const centerX = posX.value + rect.width / 2;
        const centerY = posY.value + rect.height / 2;

        posX.value = centerX < vw / 2 ? EDGE_MARGIN : vw - rect.width - EDGE_MARGIN;
        posY.value = centerY < vh / 2 ? EDGE_MARGIN : vh - rect.height - EDGE_MARGIN;

        persist();
    }

    function snap(): void {
        if (snapMode === "corner") {
            snapToCorner();
        } else {
            snapToEdge();
        }
    }

    function persist(): void {
        try {
            localStorage.setItem(key, JSON.stringify({ x: posX.value, y: posY.value }));
        } catch {
            // quota exceeded, ignore
        }
    }

    function onMove(e: MouseEvent | TouchEvent): void {
        if (!isDragging.value) {
            return;
        }
        const { cx, cy } = getClientXY(e);
        const dx = cx - startX;
        const dy = cy - startY;

        // preventDefault on a touchmove cancels the browser's compatibility click,
        // so it has to wait until the movement is a real drag rather than tap jitter.
        if (!hasMoved && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
            return;
        }
        hasMoved = true;
        e.preventDefault();

        posX.value = startPosX + dx;
        posY.value = startPosY + dy;
    }

    function onEnd(): void {
        if (!isDragging.value) {
            return;
        }
        isDragging.value = false;

        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onEnd);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
        document.removeEventListener("touchcancel", onEnd);

        if (hasMoved) {
            suppressNextClick();
            snap();
        }
    }

    // Releasing a drag over a button still fires a native click; swallow it so
    // dragging the surface never activates the control underneath.
    function suppressNextClick(): void {
        const swallow = (e: Event): void => {
            e.preventDefault();
            e.stopPropagation();
        };
        document.addEventListener("click", swallow, { capture: true, once: true });
        setTimeout(() => document.removeEventListener("click", swallow, { capture: true }), 0);
    }

    function onPointerDown(e: MouseEvent | TouchEvent): void {
        const target = e.target as HTMLElement;
        if (target.closest("[role='dialog']")) {
            return;
        }

        initPosition();
        const { cx, cy } = getClientXY(e);
        startX = cx;
        startY = cy;
        startPosX = posX.value ?? 0;
        startPosY = posY.value ?? 0;
        hasMoved = false;
        isDragging.value = true;

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onEnd);
        document.addEventListener("touchmove", onMove, { passive: false });
        document.addEventListener("touchend", onEnd);
        document.addEventListener("touchcancel", onEnd);
    }

    function onResize(): void {
        const el = elRef.value;
        if (!el || posX.value === null || posY.value === null) {
            return;
        }

        if (snapMode === "corner") {
            snapToCorner();
            return;
        }

        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        posY.value = clamp(posY.value, EDGE_MARGIN, vh - rect.height - EDGE_MARGIN);

        const centerX = posX.value + rect.width / 2;
        const ratio = centerX / vw;
        if (ratio < SNAP_LEFT_THRESHOLD) {
            posX.value = EDGE_MARGIN;
        } else if (ratio > SNAP_RIGHT_THRESHOLD) {
            posX.value = vw - rect.width - EDGE_MARGIN;
        } else {
            posX.value = (vw - rect.width) / 2;
        }

        persist();
    }

    window.addEventListener("resize", onResize);

    onMounted(onResize);

    onBeforeUnmount(() => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onEnd);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
        document.removeEventListener("touchcancel", onEnd);
        window.removeEventListener("resize", onResize);
    });

    const isPositioned = computed(() => posX.value !== null);

    const style = computed<CSSProperties>(() => {
        if (posX.value === null) {
            return {};
        }
        return {
            position: "fixed",
            left: `${posX.value}px`,
            top: `${posY.value}px`,
            zIndex: zIndex,
            transition: isDragging.value ? "none" : "left 0.3s ease, top 0.3s ease",
        };
    });

    return { elRef, style, onPointerDown, isDragging, isPositioned };
}
